import { gql } from '@apollo/client';

/**
 * Take a waiting conversation.
 *
 * The claim is what puts a human on the call: it returns the LiveKit token and
 * URL for the customer's room, so answering and connecting are one action. A
 * ticket somebody else already holds comes back with `success: false` and the
 * reason, which is the race this mutation exists to settle.
 */
export const CLAIM_ESCALATION = gql`
  mutation ClaimEscalation($ticketId: Int!) {
    claimEscalation(ticketId: $ticketId) {
      success
      reason
      livekitToken
      livekitWsUrl
      ticket {
        id
        roomName
        threadName
        subject
        conversationSummary
      }
    }
  }
`;

/** Hand it back to the queue. */
export const RELEASE_ESCALATION = gql`
  mutation ReleaseEscalation($ticketId: Int!) {
    releaseEscalation(ticketId: $ticketId) { success }
  }
`;

/* ── staff records ──────────────────────────────────────────────────────── */

/**
 * Add a member of staff.
 *
 * The input is narrower than app_admin's form suggests: the live schema takes
 * an email, a phone number, a username and the two role lists — there is no
 * `employeeType` or `field` on it, whatever app_admin still sends.
 *
 * The answer carries `emailVerificationToken`, which is how the new account is
 * actually reachable, so it is shown once and not stored.
 */
export const CREATE_EMPLOYEE = gql`
  mutation CreateEmployee($objIn: EmployeeCreateInput!) {
    createEmployee(objIn: $objIn) {
      employee {
        id
        employeeUsername
        displayName
        orgRoles
        functionalRoles
        isActive
        emailVerified
        createdAt
      }
      emailVerificationToken
    }
  }
`;

export const UPDATE_EMPLOYEE = gql`
  mutation UpdateEmployee($objIn: EmployeeUpdateInput!) {
    updateEmployee(objIn: $objIn) {
      id
      employeeUsername
      displayName
      orgRoles
      functionalRoles
      isActive
      isPaused
      emailVerified
      updatedAt
    }
  }
`;

/** Ends their access. Reversible with `reactivateEmployee`. */
export const DEACTIVATE_EMPLOYEE = gql`
  mutation DeactivateEmployee($id: Int!) {
    deactivateEmployee(id: $id) { id isActive deactivatedAt updatedAt }
  }
`;

export const REACTIVATE_EMPLOYEE = gql`
  mutation ReactivateEmployee($id: Int!) {
    reactivateEmployee(id: $id) { id isActive updatedAt }
  }
`;

/** Paused keeps the account but stops work being routed to them. */
export const PAUSE_EMPLOYEE = gql`
  mutation PauseEmployee($id: Int!, $paused: Boolean!) {
    pauseEmployee(objIn: { id: $id, paused: $paused }) { id isPaused updatedAt }
  }
`;

export const RESEND_EMPLOYEE_VERIFICATION = gql`
  mutation ResendEmployeeVerification($id: Int!) {
    resendEmployeeVerification(id: $id)
  }
`;
